/* Copyright Epic Games, Inc. All Rights Reserved. */

function maximizedLocales(locale: Intl.Locale): [string, string, string] {
  const maximized = locale.maximize();
  const language = maximized.language;
  const region = maximized.region!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const script = maximized.script!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
  const baseName = maximized.baseName;
  const languageRegion = `${language}-${region}`;
  const languageScript = `${language}-${script}`;

  return [baseName, languageRegion, languageScript];
}

export function applyNegotiationStrategy<T extends string>(
  requested: string,
  supported: T[],
  defaultLocale: T
): T {
  const requestedLocale = new Intl.Locale(requested);

  /**
   * STEP 1: Attempt exact match.
   */
  if (supported.includes(requestedLocale.baseName as T)) {
    return requestedLocale.baseName as T;
  }

  /**
   * STEP 2: Attempt language only match.
   */
  const language = requestedLocale.language;
  if (supported.includes(language as T)) {
    return language as T;
  }

  /**
   * STEP 3: Attempt maximized match.
   */
  const maximized = maximizedLocales(requestedLocale);

  for (const supportedLocale of supported) {
    for (const locale of maximized) {
      if (locale === supportedLocale) {
        return supportedLocale;
      }
    }
  }

  /**
   * STEP 4: ES override to European Spanish.
   */
  if (language === "es") {
    return "es-ES" as T;
  }

  /**
   * STEP 5: Attempt maximized language-only match.
   */
  const languageLocale = new Intl.Locale(requestedLocale.language);
  const maximizedLanguage = maximizedLocales(languageLocale);

  for (const supportedLocale of supported) {
    for (const locale of maximizedLanguage) {
      if (locale === supportedLocale) {
        return supportedLocale;
      }
    }
  }

  /** STEP 6: Attempt same language different region match. */
  for (const locale of supported) {
    if (locale.startsWith(`${language}-`)) {
      return locale;
    }
  }

  /**
   * STEP 7: Return default locale.
   */
  return defaultLocale;
}
